declare global {
  interface Window {
    Razorpay: any;
  }
}
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useLocation as useRouterLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { useLocation } from "@/contexts/LocationContext";
import { useMediaQuery } from "@/hooks/use-media-query";
import { subscriptionFormSchema } from "@/lib/schemas";
import type {
  VehicleTypeSubtypeResponse,
  VendorsResponse,
  ServicesResponse,
  PublicAdminSettingsResponse,
  CustomerProfileResponse,
  CheckoutResponse
} from "@/types/subscription";
import { Logo } from "@/assets";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Check,
  ChevronsUpDown,
  LogOut,
  MapPin,
  AlertCircle,
  RefreshCw,
  Info,
  IndianRupee,
  LocateFixed,
  User,
  Calendar as CalendarIcon,
  ChevronDown,
} from "lucide-react";
import { useEffect, useState, useCallback, useMemo } from "react";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { cn, formatDate, calculateDistance, formatTimeTo12Hour } from "@/lib/utils";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import axios from "axios";
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useVehicleSelection } from "@/hooks/useVehicleSelection";
import { useTimeSlots } from "@/hooks/useTimeSlots";
import { usePriceCalculation } from "@/hooks/usePriceCalculation";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const OTHER_VEHICLE_TYPE_ID = "other";
 
export default function BookingPage() {
  const navigate = useNavigate();
  const { state } = useRouterLocation();
  const queryClient = useQueryClient();
  
  // Utility function to format date as YYYY-MM-DD without timezone issues
  const formatDateForAPI = (date: Date): string => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const { userLocation } = useLocation();

  // Modal and Dialog States
  const [vehicleTypeDrawerOpen, setVehicleTypeDrawerOpen] = useState(false);
  const [modelTypePopoverOpen, setModelTypePopoverOpen] = useState(false);
  const [modelTypeDrawerOpen, setModelTypeDrawerOpen] = useState(false);
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [serviceDrawerOpen, setServiceDrawerOpen] = useState(false);
  const [timeSlotDrawerOpen, setTimeSlotDrawerOpen] = useState(false);
  const [billDrawerOpen, setBillDrawerOpen] = useState(false);

  // Form setup
  const subscriptionForm = useForm<z.infer<typeof subscriptionFormSchema>>({
    resolver: zodResolver(subscriptionFormSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      vendor_id: "",
      vehicle_type_id: "",
      service_mapping_id: "",
      time_slot_index: "",
      vehicle_note: "",
    },
  });

  // Check for authentication and redirect if not present
  useEffect(() => {
    // Check for auth token in localStorage
    const authToken = localStorage.getItem('timelyyy_website_auth_token');
    
    if (!authToken || !state?.customerDecodedData) {
      setIsCheckingAuth(false);
      navigate('/auth', { replace: true });
      return;
    }

    // Validate token if present
    try {
      const decodedData = jwtDecode(authToken);
      const expirationTime = decodedData.exp;
      
      if (expirationTime && expirationTime <= Date.now() / 1000) {
        // Token expired
        localStorage.removeItem('timelyyy_website_auth_token');
        setIsCheckingAuth(false);
        navigate('/auth', { replace: true });
        return;
      }
      
      // Token is valid, allow access
      setIsCheckingAuth(false);
    } catch (error) {
      // Invalid token
      localStorage.removeItem('timelyyy_website_auth_token');
      setIsCheckingAuth(false);
      navigate('/auth', { replace: true });
      return;
    }
  }, [state, navigate]);
  // Fetch customer data
  const { data: customerData, isLoading: isLoadingCustomerData } = useQuery({
    queryKey: ['customerProfile', state?.customerDecodedData?.customerId],
    queryFn: async () => {
      if (!state?.customerDecodedData?.customerId) return null;
      const response = await axios.get<CustomerProfileResponse>(`${import.meta.env.VITE_API_BASE_URL}/api/customers/public/${state.customerDecodedData.customerId}`);
      return response.data;
    },
    enabled: !!state?.customerDecodedData?.customerId
  });

  // Prefill form when customer data changes
  useEffect(() => {
    if (customerData) {
      subscriptionForm.setValue('first_name', customerData.data.fname || '');
      subscriptionForm.setValue('last_name', customerData.data.lname || '');
    }
  }, [customerData, subscriptionForm]);

  // Keep necessary state variables
  const [date, setDate] = useState<Date | undefined>(new Date());
  const [isDirty, setIsDirty] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isVerifyingPayment, setIsVerifyingPayment] = useState(false);
  const [isCurrentDateHoliday, setIsCurrentDateHoliday] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  // Function to reset all states to initial values
  const resetAllStates = useCallback(() => {
    // Reset form to default values
    subscriptionForm.reset({
      first_name: customerData?.data.fname || "",
      last_name: customerData?.data.lname || "",
      vendor_id: "",
      vehicle_type_id: "",
      service_mapping_id: "",
      time_slot_index: "",
      vehicle_note: "",
    });

    // Reset date to today
    setDate(new Date());

    // Reset all modal/drawer states
    setVehicleTypeDrawerOpen(false);
    setModelTypePopoverOpen(false);
    setModelTypeDrawerOpen(false);
    setServiceDialogOpen(false);
    setServiceDrawerOpen(false);
    setTimeSlotDrawerOpen(false);
    setBillDrawerOpen(false);

    // Reset loading states
    setIsProcessingPayment(false);
    setIsVerifyingPayment(false);

    // Reset dirty state
    setIsDirty(false);

    // Invalidate all booking-related queries to fetch fresh data
    queryClient.invalidateQueries({ queryKey: ['publicBookings'] });
    queryClient.invalidateQueries({ queryKey: ['vehicleTypes'] });
    queryClient.invalidateQueries({ queryKey: ['vendors'] });
    queryClient.invalidateQueries({ queryKey: ['services'] });
  }, [subscriptionForm, customerData?.data.fname, customerData?.data.lname, queryClient]);

  // Memoize form values that are used in multiple places
  const formValues = useMemo(() => ({
    vendorId: subscriptionForm.watch('vendor_id'),
    serviceMappingId: subscriptionForm.watch('service_mapping_id'),
  }), [subscriptionForm.watch('vendor_id'), subscriptionForm.watch('service_mapping_id')]);

  // Queries with error handling
  const {
    data: availableVehicleTypes = [],
    isError: isVehicleTypesError,
    error: vehicleTypesError,
    refetch: refetchVehicleTypes,
    isLoading: isLoadingVehicleTypes
  } = useQuery({
    queryKey: ['vehicleTypes'],
    queryFn: async () => {
      try {
        const response = await axios.get<VehicleTypeSubtypeResponse>(`${import.meta.env.VITE_API_BASE_URL}/api/vehicle-types/public`);
        return response.data.data;
      } catch (error) {
        throw error;
      }
    }
  });

  // Custom hooks - moved here after availableVehicleTypes is available
  const {
    selectedVehicleType,
    selectedSubtype,
    vehicleTypeId,
    vehicleSubtypeId,
    selectVehicleType,
    selectVehicleSubtype,
  } = useVehicleSelection({
    availableVehicleTypes
  });

  const {
    data: availableVendors = [],
    isLoading: isLoadingVendors
  } = useQuery({
    queryKey: ['vendors', userLocation?.lat, userLocation?.lon],
    queryFn: async () => {
      try {
        if (!userLocation) return [];
        const response = await axios.get<VendorsResponse>(`${import.meta.env.VITE_API_BASE_URL}/api/vendors/public/nearby?lat=${userLocation?.lat}&lng=${userLocation?.lon}`);
        return response.data.data;
      } catch (error) {
        throw error;
      }
    }
  });

  const {
    data: availableServices = []
  } = useQuery({
    queryKey: ['services', formValues.vendorId, vehicleTypeId, vehicleSubtypeId],
    queryFn: async () => {
      try {
        if (!formValues.vendorId || !vehicleTypeId || !vehicleSubtypeId) return [];
        const response = await axios.get<ServicesResponse>(`${import.meta.env.VITE_API_BASE_URL}/api/services/public/vendor/${formValues.vendorId}?vehicleTypeId=${vehicleTypeId}&vehicleSubtypeId=${vehicleSubtypeId}`);
        return response.data.data;
      } catch (error) {
        throw error;
      }
    },
    enabled: !!formValues.vendorId && !!vehicleTypeId && !!vehicleSubtypeId
  });

  const {
    data: publicAdminSettings,
    isLoading: isLoadingAdminSettings
  } = useQuery({
    queryKey: ['publicAdminSettings'],
    queryFn: async () => {
      const response = await axios.get<PublicAdminSettingsResponse>(`${import.meta.env.VITE_API_BASE_URL}/api/admins/public/settings`);
      return response.data.data;
    }
  });

  // Memoize selected vendor to prevent unnecessary recalculations
  const selectedVendor = useMemo(() =>
    availableVendors.find(vendor => vendor.vp_id === formValues.vendorId),
    [availableVendors, formValues.vendorId]
  );

  const { timeSlots, timeSlotData, isHoliday, isLoadingBookings, isFetchingBookings } = useTimeSlots({
    date,
    vendorId: formValues.vendorId,
    vendorBusinessDetails: selectedVendor?.vendor_business_details
  });

  // Check if the selected date is a holiday
  useEffect(() => {
    if (date && selectedVendor?.vendor_business_details) {
      setIsCurrentDateHoliday(isHoliday(date));
    }
  }, [date, isHoliday, selectedVendor?.vendor_business_details]);

  // Memoize selected service to prevent unnecessary recalculations
  const selectedService = useMemo(() =>
    availableServices.find(service =>
      service.vehicle_mappings.some(mapping => mapping.svm_id === formValues.serviceMappingId)
    ),
    [availableServices, formValues.serviceMappingId]
  );

  // Modify the vehicle type section to include "Other" option
  const allVehicleTypes = useMemo(() => {
    if (!availableVehicleTypes.length) return [];
    return [
      {
        vt_id: OTHER_VEHICLE_TYPE_ID,
        type_name: "Custom",
        desc: "Select this if your vehicle type is not listed",
        subtypes: []
      },
      ...availableVehicleTypes,
    ];
  }, [availableVehicleTypes]);

  // Use the price calculation hook with memoized values
  const { finalTotal, vendorPayout, hasVendorGst } = usePriceCalculation({
    serviceMappingId: vehicleTypeId === OTHER_VEHICLE_TYPE_ID ? "OTHER_VEHICLE_TYPE" : formValues.serviceMappingId!,
    vendorId: formValues.vendorId,
    availableServices,
    availableVendors,
    publicAdminSettings: publicAdminSettings!,
    isOtherService: vehicleTypeId === OTHER_VEHICLE_TYPE_ID
  });

  // Functions
  function loadScript(src: string) {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = src;
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        resolve(false);
      };
      document.body.appendChild(script);
    });
  }

  // Memoize handlers
  const handleLogout = useCallback(() => {
    localStorage.removeItem('timelyyy_website_auth_token');
    
    // Reset all states before logout
    resetAllStates();
    navigate("/auth", { replace: true });
    toast.info("Logged out successfully.");
  }, [navigate, resetAllStates]);

  const makePayment = useCallback(async (data: z.infer<typeof subscriptionFormSchema>) => {
    // Format date properly to avoid timezone issues
    const selectedDate = date ? formatDateForAPI(date) : undefined;

    const completeData = {
      ...data,
      mobile_number: customerData?.data.phone_no,
      customer_id: customerData?.data.cp_id,
      currency: "INR",
      amount: finalTotal,
      date_of_booking: selectedDate,
      service_mapping_id: data.vehicle_type_id === OTHER_VEHICLE_TYPE_ID ? undefined : data.service_mapping_id,
      vehicle_note: data.vehicle_type_id === OTHER_VEHICLE_TYPE_ID ? data.vehicle_note : undefined
    };

    setIsProcessingPayment(true);
    try {
      // Determine if this is a custom booking
      const isCustomBooking = data.vehicle_type_id === OTHER_VEHICLE_TYPE_ID;
      
      // Build the URL with query parameter for custom bookings
      const checkoutUrl = isCustomBooking 
        ? `${import.meta.env.VITE_API_BASE_URL}/api/payments/checkout?booking_type=custom`
        : `${import.meta.env.VITE_API_BASE_URL}/api/payments/checkout`;
      
      const response = await axios.post<{ success: boolean; data: CheckoutResponse }>(
        checkoutUrl,
        completeData
      );
      const checkoutData = response.data.data;
      await displayRazorpay(checkoutData);
    } catch (error) {
      toast.error('Failed to process payment. Please try again.');
    } finally {
      setIsProcessingPayment(false);
    }
  }, [customerData?.data.phone_no, customerData?.data.cp_id, finalTotal, timeSlots, date, selectedVendor, formatTimeTo12Hour, formatDateForAPI]);

  const handleRazorPayPaymentStatus = async (
    orderDetails: {
      razorpay_order_id?: string;
      razorpay_payment_id?: string;
      razorpay_signature?: string;
    } = {}
  ) => {
    setIsVerifyingPayment(true);
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/api/payments/verify`,
        {
          order_id: orderDetails.razorpay_order_id,
          payment_id: orderDetails.razorpay_payment_id,
          signature: orderDetails.razorpay_signature,
        }
      );

      if (response.status === 200) {
        toast.success("Payment successful");
        
        // Immediately invalidate booking queries to ensure fresh data
        queryClient.invalidateQueries({ queryKey: ['publicBookings'] });
        
        // Also invalidate any cached data for the current vendor and date
        if (formValues.vendorId && date) {
          queryClient.invalidateQueries({ 
            queryKey: ['publicBookings', formValues.vendorId, date.toISOString()] 
          });
        }
        
        // Reset all states before navigation
        resetAllStates();
        
        // Navigate to auth page
        navigate("/auth");
      } else {
        toast.error("Payment failed");
      }
    } catch (error) {
      toast.error("Payment failed");
    } finally {
      setIsVerifyingPayment(false);
    }
  };

  const displayRazorpay = async (data: {
    razorpay_order_id: string;
    amount: number;
    currency: string;
    customer_first_name: string;
    customer_last_name: string;
    customer_phone: string;
  }) => {
    const res = await loadScript(
      "https://checkout.razorpay.com/v1/checkout.js"
    );

    if (!res) {
      alert("Razorpay SDK failed to load. Are you online?");
      return;
    }

    const options = {
      key: import.meta.env.VITE_RAZOR_PAY_KEY_ID,
      amount: data.amount * 100,
      currency: data.currency,
      name: "Timelyyy",
      description: "Book instant washes",
      image: { Logo },
      order_id: data.razorpay_order_id,
      prefill: {
        name: `${data.customer_first_name} ${data.customer_last_name}`,
        contact: data.customer_phone,
      },
      theme: {
        color: "#43A047",
      },
      handler: async (response: {
        razorpay_payment_id: any;
        razorpay_order_id: any;
        razorpay_signature: any;
      }) => {
        handleRazorPayPaymentStatus({
          razorpay_order_id: response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature,
        });
      },
    };

    const rzp1 = new window.Razorpay(options);
    rzp1.open();
  };

  // Error Alert Component
  const ErrorAlert = ({ title, description, onRetry, className }: { title: string; description: string; onRetry?: () => void, className?: string }) => (
    <Alert className={cn(className)}>
      <div className="flex items-start justify-between w-full">
        <div className="flex gap-2">
          <AlertCircle className="size-4" />
          <div>
            <AlertTitle>{title}</AlertTitle>
            <AlertDescription>{description}</AlertDescription>
          </div>
        </div>
        {onRetry && (
          <Button
            variant="outline"
            size="icon"
            className="size-8"
            onClick={onRetry}
            type="button"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        )}
      </div>
    </Alert>
  );

  // Update selected items when data changes
  useEffect(() => {
    if (formValues.vendorId) {
      subscriptionForm.setValue("service_mapping_id", "");
      subscriptionForm.setValue("time_slot_index", "");
    }
  }, [formValues.vendorId, subscriptionForm]);

  // Clear time slot selection when date changes
  useEffect(() => {
    if (formValues.vendorId && date) {
      subscriptionForm.setValue("time_slot_index", "");
    }
  }, [date, formValues.vendorId, subscriptionForm]);

  // Update vehicle type selection
  useEffect(() => {
    const subscription = subscriptionForm.watch((value, { name }) => {
      if (name === 'vehicle_type_id') {
        // Clear service_mapping_id when switching to Other
        if (value.vehicle_type_id === OTHER_VEHICLE_TYPE_ID) {
          subscriptionForm.setValue('service_mapping_id', '');
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [subscriptionForm]);

  // to check if the form is dirty
  useEffect(() => {
    const subscription = subscriptionForm.watch(() => {
      setIsDirty(true);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [subscriptionForm]);

  // to handle if the user tries to refresh or close the page
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isDirty]);

  // Reset states when component unmounts
  useEffect(() => {
    return () => {
      // Cleanup function - reset states when component unmounts
      resetAllStates();
    };
  }, [resetAllStates]);

  // Add this effect after the other useEffect hooks
  useEffect(() => {
    // Reset service selection when vehicle type or subtype changes
    subscriptionForm.setValue('service_mapping_id', '');

    // Close all modals/drawers
    setVehicleTypeDrawerOpen(false);
    setModelTypePopoverOpen(false);
    setModelTypeDrawerOpen(false);
    setServiceDialogOpen(false);
    setServiceDrawerOpen(false);
  }, [vehicleTypeId, vehicleSubtypeId, subscriptionForm]);

  // Add effect to handle state resets when vendor changes
  useEffect(() => {
    if (formValues.vendorId) {
      subscriptionForm.setValue("service_mapping_id", "");
      subscriptionForm.setValue("time_slot_index", "");

      // Reset date to today when vendor changes to ensure calendar updates with new vendor's constraints
      setDate(new Date());

      // Close all modals/drawers
      setServiceDialogOpen(false);
      setServiceDrawerOpen(false);
      setTimeSlotDrawerOpen(false);
    }
  }, [formValues.vendorId, subscriptionForm]);

  // Check if initial data is loading
  const isInitialLoading = isLoadingVehicleTypes || isLoadingVendors || isLoadingAdminSettings || isLoadingCustomerData;

  // Show loading screen while checking authentication
  if (isCheckingAuth) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-white/80 backdrop-blur-sm">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-green-600"></div>
          <p className="text-lg font-medium text-gray-900">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-gray-50">
      <header className="flex justify-between items-center p-4 border-b-2 z-50 bg-white sticky top-0 min-h-16">
        <div 
          className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => navigate('/')}
        >
          <Avatar>
            <AvatarImage src={Logo} />
            <AvatarFallback>TI</AvatarFallback>
          </Avatar>
          <span className="text-lg font-semibold text-gray-800">Timelyyy</span>
        </div>
        
        <div className="flex items-center gap-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2 p-2 hover:bg-gray-100">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-blue-500 text-white text-sm uppercase">
                    {customerData?.data?.fname && customerData?.data?.lname ? `${customerData.data.fname?.charAt(0)}${customerData.data.lname?.charAt(0)}` : 'US'}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden sm:block text-left">
                  <div className="text-sm font-medium">
                    {customerData?.data?.fname && customerData?.data?.lname ? `${customerData.data.fname} ${customerData.data.lname}` : 'User'}
                  </div>
                  <div className="text-xs text-gray-500">
                    {customerData?.data?.phone_no ? `+91 ${customerData.data.phone_no}` : '+91 XXXXXXXXXX'}
                  </div>
                </div>
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">
                    {customerData?.data?.fname ? `${customerData.data.fname} ${customerData.data.lname || ''}` : 'User'}
                  </p>
                  <p className="text-xs leading-none text-muted-foreground">
                    {customerData?.data?.phone_no ? `+91 ${customerData.data.phone_no}` : '+91 XXXXXXXXXX'}
                  </p>
                </div>
              </DropdownMenuLabel>
              {/*<DropdownMenuSeparator />
               <DropdownMenuItem onClick={() => navigate('/profile')}>
                <User className="mr-2 h-4 w-4" />
                <span>Profile</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/bookings')}>
                <CalendarIcon className="mr-2 h-4 w-4" />
                <span>Bookings</span>
              </DropdownMenuItem> */}
              <DropdownMenuSeparator />
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <DropdownMenuItem onSelect={(e: any) => e.preventDefault()}>
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Logout</span>
                  </DropdownMenuItem>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you sure you want to logout?</AlertDialogTitle>
                    <AlertDialogDescription>
                      You will need to login again to continue booking services.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleLogout}>Logout</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
      <Form {...subscriptionForm}>
        <form onSubmit={subscriptionForm.handleSubmit(makePayment)}>
          <section className="grid grid-cols-1 lg:grid-cols-[3fr_1fr] gap-8">
            <section className="bg-gray-50 flex flex-col gap-6 p-6">
              <h2 className="text-2xl font-semibold">
                Book your <span className="text-green-500">Wash</span>
              </h2>
              <section className="flex flex-col gap-4">
                <Card className="rounded-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={subscriptionForm.control}
                    name="first_name"
                    render={({ field }) => (
                      <FormItem className="col-span-2 lg:col-span-1">
                        <FormLabel className="text-xs font-semibold text-gray-800">
                          First Name <span className="text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="First Name"
                            autoComplete="given-name"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={subscriptionForm.control}
                    name="last_name"
                    render={({ field }) => (
                      <FormItem className="col-span-2 lg:col-span-1">
                        <FormLabel className="text-xs font-semibold text-gray-800">
                          Last Name <span className="text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Last Name"
                            autoComplete="family-name"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={subscriptionForm.control}
                    name="vendor_id"
                    render={({ field }) => {
                      const [open, setOpen] = useState(false);
                      return (
                        <FormItem className="col-span-2">
                          <FormLabel className="text-xs mb-1 font-semibold text-gray-800 lg:flex items-center gap-1 justify-between">
                            <div>Wash Center <span className="text-red-500">*</span></div>
                            <div className="flex items-center gap-1 mb-1">
                              <Info className="size-4 text-green-500" />
                              <span className="text-xs text-gray-500">
                                Wash centers within {publicAdminSettings?.nearby_vendor_radius} km
                              </span>
                            </div>
                          </FormLabel>

                          {field.value ? (() => {
                            const vendor = availableVendors.find(c => c.vp_id === field.value);
                            if (!vendor) return null;

                            const distance = userLocation
                              ? calculateDistance(
                                userLocation.lat,
                                userLocation.lon,
                                vendor.vendor_business_details.latitude,
                                vendor.vendor_business_details.longitude,
                                "km"
                              )
                              : 0;

                            return (
                              <Card
                                className="flex items-center gap-2 w-full py-1 p-4 hover:bg-accent cursor-pointer"
                                onClick={() => setOpen(true)}
                              >
                                <div className="min-w-20 flex flex-col items-center justify-center gap-1 border border-green-500 rounded-md p-2 text-center">
                                  <MapPin className="size-4 text-green-600" />
                                  <span className="text-[0.7rem] text-gray-600 leading-none">
                                    {distance} km
                                  </span>
                                </div>

                                <div className="grow flex flex-col justify-center overflow-hidden">
                                  <p className="text-sm font-semibold text-gray-900 truncate">
                                    {vendor.vendor_business_details.business_name}
                                  </p>
                                  <p className="text-xs text-gray-500 truncate">
                                    {vendor.vendor_business_details.address}
                                  </p>
                                </div>
                                <ChevronsUpDown className="opacity-50 size-4" />
                              </Card>
                            );
                          })() : (
                            <Button
                              variant="outline"
                              type="button"
                              className={cn(
                                "flex justify-between items-center w-full truncate",
                                !field.value && "text-muted-foreground"
                              )}
                              onClick={() => setOpen(true)}
                            >
                              <span>Select a wash center</span>
                              <ChevronsUpDown className="opacity-50 size-4" />
                            </Button>
                          )}
                          {isDesktop ? (
                            <CommandDialog open={open} onOpenChange={setOpen}>
                              <VisuallyHidden>
                                <DialogTitle>Search Wash Centers</DialogTitle>
                                <DialogDescription>
                                  Search and select a wash center from the available options
                                </DialogDescription>
                              </VisuallyHidden>
                              <CommandInput
                                placeholder="Search wash center..."
                                className="h-9"
                              />
                              <CommandList>
                                <CommandEmpty>
                                  No wash center found.
                                </CommandEmpty>
                                <CommandGroup>
                                  {availableVendors.map((vendor) => {
                                    const distance = userLocation
                                      ? calculateDistance(
                                        userLocation.lat,
                                        userLocation.lon,
                                        vendor.vendor_business_details.latitude,
                                        vendor.vendor_business_details.longitude,
                                        "km"
                                      )
                                      : 0;

                                    const isSelected =
                                      vendor.vp_id === field.value;
                                    return (
                                      <CommandItem
                                        key={vendor.vp_id}
                                        onSelect={() => {
                                          subscriptionForm.setValue(
                                            "vendor_id",
                                            vendor.vp_id
                                          );
                                          setOpen(false);
                                        }}
                                        className={cn(
                                          "flex items-center gap-2 w-full py-1 p-4 rounded-md transition-all cursor-pointer border",
                                          isSelected
                                            ? "border-green-500 bg-green-50"
                                            : "border-gray-200 hover:bg-gray-50"
                                        )}
                                      >
                                        {/* Distance badge */}
                                        <div className="min-w-20 flex flex-col items-center justify-center gap-1 border border-green-500 rounded-md p-2 text-center">
                                          <MapPin className="size-4 text-green-600" />
                                          <span className="text-[0.7rem] text-gray-600 leading-none">
                                            {distance} km
                                          </span>
                                        </div>

                                        {/* Center name + address */}
                                        <div className="grow sm:col-span-4 flex flex-col justify-center overflow-hidden">
                                          <p className="text-sm font-semibold text-gray-900 truncate">
                                            {vendor.vendor_business_details.business_name}
                                          </p>
                                          <p className="text-xs text-gray-500 truncate">
                                            {vendor.vendor_business_details.address}
                                          </p>
                                        </div>
                                        {isSelected && (
                                          <Check className="text-green-500 w-5 h-5 sm:ml-4 self-end sm:self-center" />
                                        )}
                                      </CommandItem>
                                    );
                                  })}
                                </CommandGroup>
                              </CommandList>
                            </CommandDialog>
                          ) : (
                            <Drawer open={open} onOpenChange={setOpen}>
                              <DrawerContent>
                                <DrawerHeader className="text-left">
                                  <DrawerTitle>Choose wash center</DrawerTitle>
                                  <DrawerDescription>
                                    Select a wash center from the list below.
                                  </DrawerDescription>
                                </DrawerHeader>
                                <Command>
                                  <CommandInput
                                    placeholder="Search wash center..."
                                    className="h-9"
                                  />
                                  <CommandList>
                                    <CommandEmpty>
                                      No wash center found.
                                    </CommandEmpty>
                                    <CommandGroup>
                                      {availableVendors.map((vendor) => {
                                        const distance = userLocation
                                          ? calculateDistance(
                                            userLocation.lat,
                                            userLocation.lon,
                                            vendor.vendor_business_details.latitude,
                                            vendor.vendor_business_details.longitude,
                                            "km"
                                          )
                                          : 0;

                                        const isSelected =
                                          vendor.vp_id === field.value;
                                        return (
                                          <CommandItem
                                            key={vendor.vp_id}
                                            onSelect={() => {
                                              subscriptionForm.setValue(
                                                "vendor_id",
                                                vendor.vp_id
                                              );
                                              setOpen(false);
                                            }}
                                            className={cn(
                                              "flex items-center gap-2 w-full py-1 p-4 rounded-md transition-all cursor-pointer border",
                                              isSelected
                                                ? "border-green-500 bg-green-50"
                                                : "border-gray-200 hover:bg-gray-50"
                                            )}
                                          >
                                            {/* Distance badge */}
                                            <div className="min-w-20 flex flex-col items-center justify-center gap-1 border border-green-500 rounded-md p-2 text-center">
                                              <MapPin className="size-4 text-green-600" />
                                              <span className="text-[0.7rem] text-gray-600 leading-none">
                                                {distance} km
                                              </span>
                                            </div>

                                            {/* Center name + address */}
                                            <div className="grow sm:col-span-4 flex flex-col justify-center overflow-hidden">
                                              <p className="text-sm font-semibold text-gray-900 truncate">
                                                {vendor.vendor_business_details.business_name}
                                              </p>
                                              <p className="text-xs text-gray-500 truncate">
                                                {vendor.vendor_business_details.address}
                                              </p>
                                            </div>
                                            {isSelected && (
                                              <Check className="text-green-500 size-5" />
                                            )}
                                          </CommandItem>
                                        );
                                      })}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                                <DrawerFooter className="pt-2">
                                  <DrawerClose asChild>
                                    <Button variant="outline">Cancel</Button>
                                  </DrawerClose>
                                </DrawerFooter>
                              </DrawerContent>
                            </Drawer>
                          )}
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />
                </Card>
                <Card className="rounded-xl p-4 flex flex-col gap-4">
                  <div className="flex flex-col gap-2">
                    <div className="text-sm font-medium text-gray-800">
                      Choose vehicle type <span className="text-red-500">*</span>
                    </div>
                    {isVehicleTypesError && (
                      <ErrorAlert
                        title="Error Loading Vehicle Types"
                        description={vehicleTypesError?.message || 'Failed to load vehicle types. Please try again later.'}
                        onRetry={() => refetchVehicleTypes()}
                        className="border-red-500 text-red-500 hidden md:block"
                      />
                    )}
                    {!isVehicleTypesError && availableVehicleTypes.length === 0 && (
                      <ErrorAlert
                        title="No Vehicle Types"
                        description="No vehicle types are currently available."
                        className="border-yellow-500 text-yellow-500 hidden md:block"
                      />
                    )}

                    {/* Desktop: Radio Group Cards */}
                    <RadioGroup
                      onValueChange={(value) => {
                        subscriptionForm.setValue('vehicle_type_id', value);
                        selectVehicleType(value);
                      }}
                      value={subscriptionForm.watch('vehicle_type_id')}
                      className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-4"
                      name="vehicle_type_id"
                    >
                      {allVehicleTypes.map((vehicle) => (
                        <div key={vehicle.vt_id}>
                          <label className="font-normal">
                            <div
                              className={cn(
                                "flex items-start gap-3 border p-4 rounded-xl cursor-pointer",
                                vehicle.vt_id === vehicleTypeId
                                  ? "border-green-300 bg-green-50"
                                  : "border-gray-300"
                              )}
                            >
                              <RadioGroupItem value={vehicle.vt_id} />
                              <div className="overflow-hidden">
                                <h4 className="text-sm font-medium">{vehicle.type_name}</h4>
                                <p className="text-xs text-gray-600 mt-2 truncate">
                                  {vehicle.desc}
                                </p>
                              </div>
                            </div>
                          </label>
                        </div>
                      ))}
                    </RadioGroup>

                    {/* Mobile: Drawer */}
                    <div className="md:hidden">
                      <Drawer open={vehicleTypeDrawerOpen} onOpenChange={setVehicleTypeDrawerOpen}>
                        <DrawerTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            role="combobox"
                            className={cn(
                              "flex justify-between items-center w-full text-sm",
                              !vehicleTypeId && "text-muted-foreground"
                            )}
                          >
                            {allVehicleTypes.find(vehicle => vehicle.vt_id === vehicleTypeId)?.type_name || "Select vehicle type"}
                            <ChevronsUpDown className="opacity-50 size-4" />
                          </Button>
                        </DrawerTrigger>

                        <DrawerContent>
                          <DrawerHeader className="text-left">
                            <DrawerTitle className="text-base font-semibold">
                              Choose vehicle type
                            </DrawerTitle>
                            <DrawerDescription className="text-sm text-gray-600">
                              Select a vehicle type from the list below.
                            </DrawerDescription>
                          </DrawerHeader>

                          <Command>
                            <CommandInput placeholder="Search vehicle type..." className="h-9 text-sm" />
                            <CommandList>
                              <CommandEmpty>
                                No vehicle types found.
                              </CommandEmpty>
                              <CommandGroup>
                                {allVehicleTypes.map((vehicle) => (
                                  <CommandItem
                                    key={vehicle.vt_id}
                                    value={vehicle.type_name}
                                    onSelect={() => {
                                      selectVehicleType(vehicle.vt_id);
                                      subscriptionForm.setValue('vehicle_type_id', vehicle.vt_id);
                                      setVehicleTypeDrawerOpen(false);
                                    }}
                                    className="flex flex-col items-start py-3"
                                  >
                                    <div className="flex items-center w-full">
                                      <span className="text-sm font-medium">{vehicle.type_name}</span>
                                      <Check
                                        className={cn(
                                          "ml-auto w-4 h-4",
                                          vehicle.vt_id === vehicleTypeId ? "opacity-100" : "opacity-0"
                                        )}
                                      />
                                    </div>
                                    <p className="text-xs text-gray-600 mt-1">
                                      {vehicle.desc}
                                    </p>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>

                          <DrawerFooter className="pt-2">
                            <DrawerClose asChild>
                              <Button variant="outline" className="text-sm">
                                Cancel
                              </Button>
                            </DrawerClose>
                          </DrawerFooter>
                        </DrawerContent>
                      </Drawer>
                    </div>
                  </div>
                  <div className="col-span-2 lg:col-span-1">
                    <div className="text-xs mb-1 font-semibold text-gray-800">
                      {vehicleTypeId === OTHER_VEHICLE_TYPE_ID ? (
                        <>Vehicle Details <span className="text-red-500">*</span></>
                      ) : (
                        <>Model type <span className="text-red-500">*</span></>
                      )}
                    </div>
                    {vehicleTypeId === OTHER_VEHICLE_TYPE_ID ? (
                      <FormField
                        control={subscriptionForm.control}
                        name="vehicle_note"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Textarea
                                placeholder="Please describe your vehicle (make, model, any special requirements)"
                                {...field}
                                className="min-h-[100px]"
                                required
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    ) : (
                      <>
                        {isDesktop ? (
                          <Popover open={modelTypePopoverOpen} onOpenChange={setModelTypePopoverOpen}>
                            <PopoverTrigger asChild>
                              <Button
                                type="button"
                                variant="outline"
                                role="combobox"
                                className={cn(
                                  "flex justify-between items-center w-full text-sm",
                                  !vehicleSubtypeId && "text-muted-foreground"
                                )}
                              >
                                {availableVehicleTypes.find(vehicle => vehicle.vt_id === vehicleTypeId)?.subtypes.find(subtype => subtype.vst_id === vehicleSubtypeId)?.subtype_name || "Select model type"}
                                <ChevronsUpDown className="opacity-50 size-4" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="p-0" align="start">
                              <Command>
                                <CommandInput placeholder="Search model type..." className="h-9 text-sm" />
                                <CommandList>
                                  <CommandEmpty>
                                    No model types found.
                                  </CommandEmpty>
                                  <CommandGroup>
                                    {availableVehicleTypes.find(vehicle => vehicle.vt_id === vehicleTypeId)?.subtypes.map((subtype) => (
                                      <CommandItem key={subtype.vst_id} value={subtype.subtype_name} onSelect={() => {
                                        selectVehicleSubtype(subtype.vst_id);
                                        setModelTypePopoverOpen(false);
                                      }} className="flex flex-col items-start py-3">
                                        <div className="flex items-center w-full">
                                          <span className="text-sm font-medium">{subtype.subtype_name}</span>
                                          <Check
                                            className={cn(
                                              "ml-auto w-4 h-4",
                                              subtype.vst_id === vehicleSubtypeId ? "opacity-100" : "opacity-0"
                                            )}
                                          />
                                        </div>
                                        <p className="text-xs text-gray-600 mt-1">
                                          {subtype.desc}
                                        </p>
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        ) : (
                          <Drawer open={modelTypeDrawerOpen} onOpenChange={setModelTypeDrawerOpen}>
                            <DrawerTrigger asChild>
                              <Button
                                type="button"
                                variant="outline"
                                role="combobox"
                                className={cn(
                                  "flex justify-between items-center w-full text-sm",
                                  !vehicleSubtypeId && "text-muted-foreground"
                                )}
                              >
                                {availableVehicleTypes.find(vehicle => vehicle.vt_id === vehicleTypeId)?.subtypes.find(subtype => subtype.vst_id === vehicleSubtypeId)?.subtype_name || "Select model type"}
                                <ChevronsUpDown className="opacity-50 size-4" />
                              </Button>
                            </DrawerTrigger>
                            <DrawerContent>
                              <DrawerHeader className="text-left">
                                <DrawerTitle>Choose model type</DrawerTitle>
                                <DrawerDescription>
                                  Select a model type from the list below.
                                </DrawerDescription>
                              </DrawerHeader>
                              <Command>
                                <CommandInput placeholder="Search model type..." className="h-9 text-sm" />
                                <CommandList>
                                  <CommandEmpty>
                                    No model types found.
                                  </CommandEmpty>
                                  <CommandGroup>
                                    {availableVehicleTypes.find(vehicle => vehicle.vt_id === vehicleTypeId)?.subtypes.map((subtype) => (
                                      <CommandItem key={subtype.vst_id} value={subtype.subtype_name} onSelect={() => {
                                        selectVehicleSubtype(subtype.vst_id);
                                        setModelTypeDrawerOpen(false);
                                      }} className="flex flex-col items-start py-3">
                                        <div className="flex items-center w-full">
                                          <span className="text-sm font-medium">{subtype.subtype_name}</span>
                                          <Check
                                            className={cn(
                                              "ml-auto w-4 h-4",
                                              subtype.vst_id === vehicleSubtypeId ? "opacity-100" : "opacity-0"
                                            )}
                                          />
                                        </div>
                                        <p className="text-xs text-gray-600 mt-1">
                                          {subtype.desc}
                                        </p>
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                              <DrawerFooter className="pt-2">
                                <DrawerClose asChild>
                                  <Button variant="outline" className="text-sm">
                                    Cancel
                                  </Button>
                                </DrawerClose>
                              </DrawerFooter>
                            </DrawerContent>
                          </Drawer>
                        )}
                      </>
                    )}
                  </div>
                  {vehicleTypeId !== OTHER_VEHICLE_TYPE_ID && (
                    <FormField
                      control={subscriptionForm.control}
                      name="service_mapping_id"
                      render={({ field }) => (
                        <FormItem className="col-span-2">
                          <FormLabel htmlFor="service-select" className="text-xs mb-1 font-semibold text-gray-800">
                            Service <span className="text-red-500">*</span>
                          </FormLabel>

                          {field.value ? (() => {
                            const service = availableServices.find(service =>
                              service.vehicle_mappings.some(mapping => mapping.svm_id === field.value)
                            );
                            if (!service) return null;

                            const mapping = service.vehicle_mappings.find(m => m.svm_id === field.value);
                            if (!mapping) return null;

                            return (
                              <Card
                                id="service-select"
                                className="flex items-center gap-2 w-full py-1 p-4 hover:bg-accent cursor-pointer"
                                onClick={() => isDesktop ? setServiceDialogOpen(true) : setServiceDrawerOpen(true)}
                              >
                                <div className="min-w-20 flex flex-col items-center justify-center gap-1 border border-green-500 rounded-md p-2 text-center">
                                  <IndianRupee className="size-4 text-green-600" />
                                  <span className="text-xs text-gray-600 leading-none">
                                    {mapping.service_cost}
                                  </span>
                                </div>

                                <div className="grow flex flex-col justify-center overflow-hidden">
                                  <p className="text-sm font-semibold text-gray-900 truncate">
                                    {service.service_name}
                                  </p>
                                  <p className="text-xs text-gray-500 truncate">
                                    {service.desc}
                                  </p>
                                </div>
                                <ChevronsUpDown className="opacity-50 size-4" />
                              </Card>
                            );
                          })() : (
                            <Button
                              id="service-select"
                              variant="outline"
                              type="button"
                              className={cn(
                                "flex justify-between items-center w-full truncate",
                                !field.value && "text-muted-foreground"
                              )}
                              onClick={() => isDesktop ? setServiceDialogOpen(true) : setServiceDrawerOpen(true)}
                            >
                              <span>Select a service</span>
                              <ChevronsUpDown className="opacity-50 size-4" />
                            </Button>
                          )}

                          {isDesktop ? (
                            <CommandDialog open={serviceDialogOpen} onOpenChange={setServiceDialogOpen}>
                              <VisuallyHidden>
                                <DialogTitle>Search Services</DialogTitle>
                                <DialogDescription>
                                  Search and select a service from the available options
                                </DialogDescription>
                              </VisuallyHidden>
                              <CommandInput
                                placeholder="Search service..."
                                className="h-9"
                              />
                              <CommandList>
                                <CommandEmpty>No services found.</CommandEmpty>
                                <CommandGroup>
                                  {availableServices.map((service) => (
                                    service.vehicle_mappings.map((mapping) => (
                                      <CommandItem
                                        key={mapping.svm_id}
                                        onSelect={() => {
                                          subscriptionForm.setValue("service_mapping_id", mapping.svm_id);
                                          setServiceDialogOpen(false);
                                        }}
                                        className={cn(
                                          "flex items-center gap-2 w-full py-1 p-4 rounded-md transition-all cursor-pointer border",
                                          mapping.svm_id === field.value
                                            ? "border-green-500 bg-green-50"
                                            : "border-gray-200 hover:bg-gray-50"
                                        )}
                                      >
                                        <div className="min-w-20 flex flex-col items-center justify-center gap-1 border border-green-500 rounded-md p-2 text-center">
                                          <IndianRupee className="size-4 text-green-600" />
                                          <span className="text-xs text-gray-600 leading-none">
                                            {mapping.service_cost}
                                          </span>
                                        </div>

                                        <div className="grow sm:col-span-4 flex flex-col justify-center overflow-hidden">
                                          <p className="text-sm font-semibold text-gray-900 truncate">
                                            {service.service_name}
                                          </p>
                                          <p className="text-xs text-gray-500 truncate">
                                            {service.desc}
                                          </p>
                                        </div>
                                        {mapping.svm_id === field.value && (
                                          <Check className="text-green-500 w-5 h-5 sm:ml-4 self-end sm:self-center" />
                                        )}
                                      </CommandItem>
                                    ))
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </CommandDialog>
                          ) : (
                            <Drawer open={serviceDrawerOpen} onOpenChange={setServiceDrawerOpen}>
                              <DrawerContent>
                                <DrawerHeader className="text-left">
                                  <DrawerTitle>Choose service</DrawerTitle>
                                  <DrawerDescription>
                                    Select a service from the list below.
                                  </DrawerDescription>
                                </DrawerHeader>
                                <Command>
                                  <CommandInput
                                    placeholder="Search service..."
                                    className="h-9"
                                  />
                                  <CommandList>
                                    <CommandEmpty>No services found.</CommandEmpty>
                                    <CommandGroup>
                                      {availableServices.map((service) => (
                                        service.vehicle_mappings.map((mapping) => (
                                          <CommandItem
                                            key={mapping.svm_id}
                                            onSelect={() => {
                                              subscriptionForm.setValue("service_mapping_id", mapping.svm_id);
                                              setServiceDrawerOpen(false);
                                            }}
                                            className={cn(
                                              "flex items-center gap-2 w-full py-1 p-4 rounded-md transition-all cursor-pointer border",
                                              mapping.svm_id === field.value
                                                ? "border-green-500 bg-green-50"
                                                : "border-gray-200 hover:bg-gray-50"
                                            )}
                                          >
                                            <div className="min-w-20 flex flex-col items-center justify-center gap-1 border border-green-500 rounded-md p-2 text-center">
                                              <IndianRupee className="size-4 text-green-600" />
                                              <span className="text-xs text-gray-600 leading-none">
                                                {mapping.service_cost}
                                              </span>
                                            </div>

                                            <div className="grow sm:col-span-4 flex flex-col justify-center overflow-hidden">
                                              <p className="text-sm font-semibold text-gray-900 truncate">
                                                {service.service_name}
                                              </p>
                                              <p className="text-xs text-gray-500 truncate">
                                                {service.desc}
                                              </p>
                                            </div>
                                            {mapping.svm_id === field.value && (
                                              <Check className="text-green-500 size-5" />
                                            )}
                                          </CommandItem>
                                        ))
                                      ))}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                                <DrawerFooter className="pt-2">
                                  <DrawerClose asChild>
                                    <Button variant="outline">Cancel</Button>
                                  </DrawerClose>
                                </DrawerFooter>
                              </DrawerContent>
                            </Drawer>
                          )}
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </Card>
                <Card className="rounded-xl p-4">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-xs font-semibold text-gray-800">
                      Choose time slot <span className="text-red-500">*</span>
                    </p>
                    {selectedVendor && (
                      <div className="flex flex-col gap-1">
                        <div className="text-xs text-gray-500 flex items-center gap-1">
                          <Info className="size-3" />
                          <span>
                            {formatTimeTo12Hour(selectedVendor.vendor_business_details.opens_at)} - {formatTimeTo12Hour(selectedVendor.vendor_business_details.closes_at)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {!formValues.vendorId ? (
                    <div className="flex items-center justify-center p-8 text-gray-500">
                      <div className="text-center">
                        <LocateFixed className="size-8 mx-auto mb-2 text-gray-400" />
                        <p className="text-sm">Please select a wash center first</p>
                      </div>
                    </div>
                  ) : (
                    <section className="flex justify-between gap-2">
                      <div className="flex-1">
                        <CalendarComponent
                          mode="single"
                          selected={date}
                          onSelect={(newDate) => {
                            if (newDate) {
                              const isDateChanged = newDate.toDateString() !== date?.toDateString();
                              
                              // If selecting a holiday, show message
                              if (isHoliday(newDate)) {
                                setDate(newDate);
                                setIsCurrentDateHoliday(true);
                                subscriptionForm.setValue("time_slot_index", "");
                                
                                // Show drawer on mobile even for holidays to display the message
                                if (!isDesktop) {
                                  setTimeSlotDrawerOpen(true);
                                }
                                return;
                              }
                              
                              setDate(newDate);
                              setIsCurrentDateHoliday(false);
                              
                              // Clear time slot selection only when date actually changes
                              if (isDateChanged) {
                                subscriptionForm.setValue("time_slot_index", "");
                              }
                            }
                          }}
                          onDayClick={() => {
                            // Force drawer to open on mobile when any day is clicked, even if it's the same date
                            if (!isDesktop) {
                              setTimeSlotDrawerOpen(true);
                            }
                          }}
                          classNames={{
                            day_today: "bg-green-600 hover:bg-green-600 text-white hover:text-white",
                            day_selected: "border-2 border-green-500",
                            day_outside: "text-gray-400 rounded-md",
                            day_disabled: "bg-gray-100 text-gray-400 cursor-not-allowed",
                          }}
                          disabled={(checkDate) => {
                            // Disable past dates (compare dates without time)
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            const checkDateOnly = new Date(checkDate);
                            checkDateOnly.setHours(0, 0, 0, 0);
                            
                            if (checkDateOnly < today) {
                              return true;
                            }
                            // Disable dates beyond the booking limit
                            const maxDate = new Date();
                            maxDate.setDate(maxDate.getDate() + (publicAdminSettings?.pre_book_days || 7));
                            maxDate.setHours(23, 59, 59, 999); // End of max date
                            if (checkDateOnly > maxDate) {
                              return true;
                            }
                            // Don't disable holidays - let users select them to see the holiday message
                            return false;
                          }}
                          fromDate={(() => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            return today;
                          })()}
                          toDate={(() => {
                            const maxDate = new Date();
                            maxDate.setDate(maxDate.getDate() + (publicAdminSettings?.pre_book_days || 7));
                            maxDate.setHours(23, 59, 59, 999);
                            return maxDate;
                          })()}
                          modifiers={{
                            holiday: (checkDate) => isHoliday(checkDate),
                            weeklyHoliday: (checkDate) => timeSlotData.weeklyHolidays.includes(checkDate.getDay()),
                            specificHoliday: (checkDate) => {
                              // Format date as YYYY-MM-DD to match backend format
                              const dateString = formatDateForAPI(checkDate);
                              return timeSlotData.holidays.some((holiday: any) => {
                                // Handle object format: { date: "YYYY-MM-DD", name: "Holiday Name", isRecurring: true }
                                if (typeof holiday === 'object' && holiday !== null && holiday.date) {
                                  const holidayDate = holiday.date.includes('T') ? holiday.date.split('T')[0] : holiday.date;
                                  
                                  // For recurring holidays, check if the month and day match (ignoring year)
                                  if (holiday.isRecurring) {
                                    const holidayDateObj = new Date(holidayDate);
                                    const checkDateObj = new Date(checkDate);
                                    
                                    // Compare month and day (0-indexed)
                                    return holidayDateObj.getMonth() === checkDateObj.getMonth() && 
                                           holidayDateObj.getDate() === checkDateObj.getDate();
                                  }
                                  
                                  // For non-recurring holidays, check exact date match
                                  return holidayDate === dateString;
                                }
                                return false;
                              });
                            },
                          }}
                          modifiersStyles={{
                            holiday: {
                              backgroundColor: '#fee2e2',
                              color: '#dc2626',
                              textDecoration: 'line-through',
                            },
                            weeklyHoliday: {
                              backgroundColor: '#fef3c7',
                              color: '#d97706',
                            },
                            specificHoliday: {
                              backgroundColor: '#fee2e2',
                              color: '#dc2626',
                              textDecoration: 'line-through',
                            },
                          }}
                        />
                        
                        {/* Mobile: Show selected time slot */}
                        {!isDesktop && subscriptionForm.watch('time_slot_index') && (
                          <div className="mt-4">
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                  <span className="text-sm font-medium text-green-800">Selected Time Slot</span>
                                </div>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="text-green-600 hover:text-green-700 px-2 h-auto py-1"
                                  onClick={() => setTimeSlotDrawerOpen(true)}
                                >
                                  Change
                                </Button>
                              </div>
                              {(() => {
                                const selectedSlot = timeSlots.find(slot => slot.slotIndex === subscriptionForm.watch('time_slot_index'));
                                return selectedSlot ? (
                                  <div className="mt-2">
                                    <span className="text-lg font-semibold text-green-700">
                                      {selectedSlot.startTime}
                                    </span>
                                    <span className="text-sm text-gray-600 ml-2">
                                      on {date?.toLocaleDateString('en-US', { 
                                        weekday: 'short', 
                                        month: 'short', 
                                        day: 'numeric' 
                                      })}
                                    </span>
                                  </div>
                                ) : null;
                              })()}
                            </div>
                          </div>
                        )}
                      </div>
                      {isDesktop && (
                        <FormField
                          control={subscriptionForm.control}
                          name="time_slot_index"
                          render={({ field }) => (
                            <FormItem className="flex flex-col gap-1 w-full">
                              <FormControl>
                                <ScrollArea className="h-[400px] p-4 border rounded-lg shadow-sm">
                                  <RadioGroup
                                    onValueChange={(value) => {
                                      field.onChange(value);
                                      if (!isDesktop) {
                                        setTimeSlotDrawerOpen(false);
                                      }
                                    }}
                                    defaultValue={field.value}
                                    className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                                    {isLoadingBookings || isFetchingBookings ? (
                                      <div className="col-span-full text-center text-gray-500 py-8">
                                        <div className="flex flex-col items-center gap-2">
                                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                                          <p className="text-sm">Loading available time slots...</p>
                                        </div>
                                      </div>
                                    ) : isCurrentDateHoliday ? (
                                      <div className="col-span-full text-center text-red-500 py-8">
                                        <div className="flex flex-col items-center gap-2">
                                          <AlertCircle className="size-8 text-red-400" />
                                          <p className="text-sm font-semibold">Selected date is a holiday</p>
                                          <p className="text-xs text-gray-600">No time slots available on this date</p>
                                          <p className="text-xs text-gray-500">Please select a different date</p>
                                        </div>
                                      </div>
                                    ) : timeSlots.length === 0 ? (
                                      <div className="col-span-full text-center text-gray-500 py-8">
                                        <div className="flex flex-col items-center gap-2">
                                          <AlertCircle className="size-8 text-gray-400" />
                                          <p className="text-sm">No time slots available for this date</p>
                                          <p className="text-xs">Try selecting a different date</p>
                                        </div>
                                      </div>
                                    ) : (
                                      timeSlots.map((slot) => {
                                        const isUnavailable = timeSlotData.unavailableSlots.includes(Number(slot.slotIndex));
                                        const isBooked = timeSlotData.bookedSlots.includes(Number(slot.slotIndex));
                                        const isSelected = field.value === slot.slotIndex;
                                        return (
                                          <FormItem key={slot.slotIndex}>
                                            <FormLabel
                                              className={cn(
                                                "w-full flex items-center justify-between p-4 border rounded-lg cursor-pointer",
                                                isSelected && "bg-green-100 border-green-400",
                                                !isSelected && !isUnavailable && !isBooked && "border-gray-400 hover:bg-gray-50",
                                                (isUnavailable || isBooked) && "opacity-50 bg-gray-100"
                                              )}
                                            >
                                              <div className="flex items-center gap-4">
                                                <FormControl>
                                                  <RadioGroupItem
                                                    value={slot.slotIndex}
                                                    disabled={isUnavailable || isBooked}
                                                  />
                                                </FormControl>
                                                <div className="flex flex-col">
                                                  <span className="text-sm font-medium">
                                                    {slot.startTime}
                                                  </span>
                                                  {isUnavailable ? (
                                                    <span className="text-xs text-gray-500">Unavailable</span>
                                                  ) : isBooked ? (
                                                    <span className="text-xs text-yellow-600">Booked</span>
                                                  ) : (
                                                    <span className="text-xs text-green-600">Available</span>
                                                  )}
                                                </div>
                                              </div>
                                            </FormLabel>
                                          </FormItem>
                                        );
                                      }))}
                                  </RadioGroup>
                                </ScrollArea>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}
                      {!isDesktop && (
                        <Drawer
                          open={timeSlotDrawerOpen}
                          onOpenChange={setTimeSlotDrawerOpen}
                        >
                          <DrawerContent>
                            <DrawerHeader className="text-left">
                              <DrawerTitle>Select a time slot</DrawerTitle>
                              <DrawerDescription>
                                Choose an available time slot for the selected date.
                              </DrawerDescription>
                            </DrawerHeader>

                            <div className="px-4 pb-4">
                              <FormField
                                control={subscriptionForm.control}
                                name="time_slot_index"
                                render={({ field }) => (
                                  <FormItem className="space-y-4 w-full">
                                    <FormControl>
                                      <ScrollArea className="h-[400px] p-4 border rounded-lg shadow-sm">
                                        <RadioGroup
                                          onValueChange={(value) => {
                                            field.onChange(value);
                                            setTimeSlotDrawerOpen(false);
                                          }}
                                          defaultValue={field.value}
                                          className="grid grid-cols-1 gap-2"
                                        >
                                          {isLoadingBookings || isFetchingBookings ? (
                                            <div className="text-center text-gray-500 py-8">
                                              <div className="flex flex-col items-center gap-2">
                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                                                <p className="text-sm">Loading available time slots...</p>
                                              </div>
                                            </div>
                                          ) : isCurrentDateHoliday ? (
                                            <div className="text-center text-red-500 py-8">
                                              <div className="flex flex-col items-center gap-2">
                                                <AlertCircle className="size-8 text-red-400" />
                                                <p className="text-sm font-semibold">Selected date is a holiday</p>
                                                <p className="text-xs text-gray-600">No time slots available on this date</p>
                                                <p className="text-xs text-gray-500">Please select a different date</p>
                                              </div>
                                            </div>
                                          ) : timeSlots.length === 0 ? (
                                            <div className="text-center text-gray-500 py-8">
                                              <div className="flex flex-col items-center gap-2">
                                                <AlertCircle className="size-8 text-gray-400" />
                                                <p className="text-sm">No time slots available for this date</p>
                                                <p className="text-xs">Try selecting a different date</p>
                                              </div>
                                            </div>
                                          ) : (
                                            timeSlots.map((slot) => {
                                              const isUnavailable = timeSlotData.unavailableSlots.includes(Number(slot.slotIndex));
                                              const isBooked = timeSlotData.bookedSlots.includes(Number(slot.slotIndex));
                                              const isSelected = field.value === slot.slotIndex;

                                              return (
                                                <FormItem key={slot.slotIndex}>
                                                  <FormLabel
                                                    className={cn(
                                                      "w-full flex items-center justify-between p-4 border rounded-lg cursor-pointer",
                                                      isSelected && "bg-green-100 border-green-400",
                                                      !isSelected && !isUnavailable && !isBooked && "border-gray-400 hover:bg-gray-50",
                                                      (isUnavailable || isBooked) && "opacity-50 bg-gray-100"
                                                    )}
                                                  >
                                                    <div className="flex items-center gap-4">
                                                      <FormControl>
                                                        <RadioGroupItem
                                                          value={slot.slotIndex}
                                                          disabled={isUnavailable || isBooked}
                                                        />
                                                      </FormControl>
                                                      <div className="flex flex-col">
                                                        <span className="text-sm font-medium">
                                                          {slot.startTime}
                                                        </span>
                                                        {isUnavailable ? (
                                                          <span className="text-xs text-gray-500">Unavailable</span>
                                                        ) : isBooked ? (
                                                          <span className="text-xs text-yellow-600">Booked</span>
                                                        ) : (
                                                          <span className="text-xs text-green-600">Available</span>
                                                        )}
                                                      </div>
                                                    </div>
                                                  </FormLabel>
                                                </FormItem>
                                              );
                                            }))}
                                        </RadioGroup>
                                      </ScrollArea>
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                            <DrawerFooter>
                              <DrawerClose asChild>
                                <Button variant="outline">Close</Button>
                              </DrawerClose>
                            </DrawerFooter>
                          </DrawerContent>
                        </Drawer>
                      )}
                    </section>
                  )}
                </Card>
              </section>
            </section>

            {/* billing section never touch this section */}
            <aside className="p-6 sticky top-16 h-fit min-h-[calc(100vh-4rem)] bg-green-50 border-l-2 hidden lg:flex flex-col gap-4">
              <section className="flex-grow gap-4">
                <h3 className="text-2xl font-semibold mb-4">
                  Timely <span className="text-green-700"></span>
                </h3>
                <span className="text-xs">
                  {formatDate(new Date())}
                </span>
                {selectedSubtype && selectedService && vehicleTypeId !== OTHER_VEHICLE_TYPE_ID && (
                  <section className="flex justify-between mb-2 pb-2">
                    <div className="flex flex-col text-sm">
                      <span>
                        {selectedService?.service_name} ({selectedVehicleType?.type_name})
                      </span>
                      <span className="text-xs">
                        {selectedSubtype?.subtype_name}
                      </span>
                    </div>
                    <span className="flex items-center gap-1"><IndianRupee className="size-3" />{selectedService?.vehicle_mappings.find(
                      mapping => mapping.svm_id === subscriptionForm.watch('service_mapping_id')
                    )?.service_cost || 0}</span>
                  </section>
                )}
                {vehicleTypeId === OTHER_VEHICLE_TYPE_ID && selectedVendor && (
                  <section className="flex justify-between mb-2 pb-2">
                    <div className="flex flex-col text-sm">
                      <span>Custom Vehicle Booking</span>
                      <span className="text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <span>Min. booking charge</span>
                          <Popover>
                            <PopoverTrigger asChild>
                              <button type="button" className="ml-1 text-gray-400 hover:text-gray-600">
                                <Info className="size-3" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="text-xs max-w-xs">
                              Final price may change after service. You may get a refund or pay extra based on actual work.
                            </PopoverContent>
                          </Popover>
                        </span>
                      </span>
                    </div>
                    <span className="flex items-center gap-1">
                      <IndianRupee className="size-3" />{selectedVendor.vendor_settings.booking_charges || 0}
                    </span>
                  </section>
                )}
              </section>
              <section className="mt-auto">
                <hr className="my-2" />
                <div className="flex justify-between mb-2">
                  <span className="text-xs">Service Amount {hasVendorGst ? "+ GST " + publicAdminSettings?.gst_value + "%" : ""}</span>
                  <span className="flex items-center gap-1"><IndianRupee className="size-3" />{vendorPayout.toFixed(2)}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-xs">Platform Fees + Other Taxes</span>
                  <span className="flex items-center gap-1"><IndianRupee className="size-3" />{Number(finalTotal - vendorPayout).toFixed(2)}</span>
                </div>
                <hr className="my-4" />
                <div className="flex justify-between font-semibold text-xl">
                  <span>Total</span>
                  <span className="flex items-center gap-1"><IndianRupee className="size-3" />{finalTotal.toFixed(2)}</span>
                </div>
                <Button
                  type="submit"
                  disabled={isProcessingPayment || isVerifyingPayment}
                  className="w-full mt-4 bg-green-600 hover:bg-green-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessingPayment ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Processing...
                    </>
                  ) : isVerifyingPayment ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Verifying...
                    </>
                  ) : (
                    <>
                      Pay <IndianRupee className="size-3" />{finalTotal.toFixed(2)}
                    </>
                  )}
                </Button>
              </section>
            </aside>
            <Drawer open={billDrawerOpen} onOpenChange={setBillDrawerOpen}>
              <DrawerTrigger asChild>
                <Button className="w-full bg-green-600 hover:bg-green-700 text-white p-4 sticky bottom-0 lg:hidden rounded-none">
                  View Bill
                </Button>
              </DrawerTrigger>
              <DrawerContent className="h-[85vh]">
                <DrawerHeader>
                  <DrawerTitle>Billing Details</DrawerTitle>
                  <DrawerDescription>Timely</DrawerDescription>
                </DrawerHeader>
                <div className="p-4 pb-0 rounded-lg h-full flex flex-col">
                  <section className="flex-grow">
                    <h3 className="text-2xl font-semibold mb-4">
                      Timely <span className="text-green-700"></span>
                    </h3>
                    <span className="text-xs">
                      {formatDate(new Date())}
                    </span>
                    {selectedSubtype && selectedService && vehicleTypeId !== OTHER_VEHICLE_TYPE_ID && (
                      <section className="flex justify-between mb-2 pb-2">
                        <div className="flex flex-col text-sm">
                          <span>
                            {selectedService?.service_name} ({selectedVehicleType?.type_name})
                          </span>
                          <span className="text-xs">
                            {selectedSubtype?.subtype_name}
                          </span>
                        </div>
                        <span className="flex items-center gap-1"><IndianRupee className="size-3" />{selectedService?.vehicle_mappings.find(
                          mapping => mapping.svm_id === subscriptionForm.watch('service_mapping_id')
                        )?.service_cost || 0}</span>
                      </section>
                    )}
                    {vehicleTypeId === OTHER_VEHICLE_TYPE_ID && selectedVendor && (
                      <section className="flex justify-between mb-2 pb-2">
                        <div className="flex flex-col text-sm">
                          <span>Custom Vehicle Booking</span>
                          <span className="text-xs text-gray-500">
                            Minimum booking charge - Final cost may vary after inspection
                          </span>
                        </div>
                        <span className="flex items-center gap-1">
                          <IndianRupee className="size-3" />{selectedVendor.vendor_settings.booking_charges || 0}
                        </span>
                      </section>
                    )}
                  </section>
                  <section className="mt-auto">
                    <hr className="my-2" />
                    <div className="flex justify-between mb-2">
                      <span className="text-xs">Service Amount {hasVendorGst ? "+ GST " + publicAdminSettings?.gst_value + "%" : ""}</span>
                      <span className="flex items-center gap-1"><IndianRupee className="size-3" />{vendorPayout.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between mb-2">
                      <span className="text-xs">Platform Fees + Other Taxes</span>
                      <span className="flex items-center gap-1"><IndianRupee className="size-3" />{Number(finalTotal - vendorPayout).toFixed(2)}</span>
                    </div>
                    <hr className="my-4" />
                    <div className="flex justify-between font-semibold text-xl">
                      <span>Total</span>
                      <span className="flex items-center gap-1"><IndianRupee className="size-3" />{finalTotal.toFixed(2)}</span>
                    </div>
                    <Button
                      type="button"
                      onClick={subscriptionForm.handleSubmit(makePayment)}
                      disabled={isProcessingPayment || isVerifyingPayment}
                      className="w-full mt-4 bg-green-600 hover:bg-green-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isProcessingPayment ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Processing...
                        </>
                      ) : isVerifyingPayment ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Verifying...
                        </>
                      ) : (
                        <>
                          Pay <IndianRupee className="size-3" />{finalTotal.toFixed(2)}
                        </>
                      )}
                    </Button>
                  </section>
                </div>
                <DrawerFooter>
                  <DrawerClose asChild>
                    <Button variant="outline" className="w-full">
                      Cancel
                    </Button>
                  </DrawerClose>
                </DrawerFooter>
              </DrawerContent>
            </Drawer>
          </section>
        </form>
      </Form>
      
      {/* Loading Modal */}
      <Dialog open={isProcessingPayment || isVerifyingPayment}>
        <DialogContent className="sm:max-w-md" hideClose>
          <div className="flex flex-col items-center gap-4 py-6">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
            <div className="text-center">
              <DialogTitle className="text-lg font-semibold text-gray-900">
                {isProcessingPayment ? "Processing Payment" : "Verifying Payment"}
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-600 mt-2">
                {isProcessingPayment 
                  ? "Please wait while we prepare your payment..." 
                  : "Please wait while we verify your payment..."
                }
              </DialogDescription>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Initial Loading Overlay */}
      {isInitialLoading && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-white/80 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-green-600"></div>
            <p className="text-lg font-medium text-gray-900">Loading...</p>
          </div>
        </div>
      )}
    </main>
  );
};